<template>
  <v-dialog
    v-model="localModel"
    max-width="560"
    data-test="telegram-join-modal"
  >
    <v-card class="relative rounded-xl overflow-hidden bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700">
      <div
        v-if="showConfetti"
        class="pointer-events-none absolute inset-x-0 top-0 h-44 overflow-hidden z-10"
        aria-hidden="true"
      >
        <span
          v-for="piece in confettiPieces"
          :key="`${confettiRunId}-${piece.id}`"
          class="confetti-piece"
          :style="confettiStyle(piece)"
        />
      </div>

      <v-card-title class="px-6 py-4 bg-sky-50 dark:bg-sky-900/20 border-b border-sky-100 dark:border-slate-700">
        <div class="flex items-center gap-2">
          <PhTelegramLogo :size="22" class="text-sky-600" />
          <span class="text-base sm:text-lg font-semibold text-gray-900 dark:text-slate-100">
            {{ t('telegramModal.title') }}
          </span>
        </div>
      </v-card-title>

      <v-card-text class="px-6 py-6">
        <p class="text-sm sm:text-base text-gray-700 dark:text-slate-300 mb-4">
          {{ t('telegramModal.description', { name: telegramGroupName }) }}
        </p>

        <div class="bg-sky-50 dark:bg-slate-800 border border-sky-200 dark:border-slate-700 rounded-lg p-4 mb-4">
          <p class="text-xs sm:text-sm text-sky-700 dark:text-sky-300 mb-3">
            {{ t('telegramModal.scanHint') }}
          </p>
          <div v-if="qrCodeUrl" class="flex justify-center mb-3">
            <img
              :src="qrCodeUrl"
              :alt="t('telegramModal.qrAlt')"
              class="w-44 h-44 rounded-lg border border-sky-100 dark:border-slate-600 bg-white p-2"
            >
          </div>

          <a
            v-if="invitationLink"
            :href="invitationLink"
            target="_blank"
            rel="noopener noreferrer"
            class="block w-full text-center px-4 py-2 rounded-lg bg-sky-600 text-white text-sm font-medium hover:bg-sky-700 transition-colors"
            data-test="telegram-join-link"
          >
            {{ t('telegramModal.joinLink') }}
          </a>

          <p v-else class="text-xs text-slate-500 dark:text-slate-400 text-center">
            {{ t('telegramModal.linkNotAvailable') }}
          </p>
        </div>
      </v-card-text>

      <v-card-actions class="px-6 pb-6 pt-0">
        <div class="w-full grid grid-cols-1 sm:grid-cols-2 gap-2">
          <v-btn
            variant="flat"
            class="rounded-lg !normal-case font-semibold !bg-gray-100 !text-gray-700 hover:!bg-gray-200 dark:!bg-slate-800 dark:!text-slate-200 dark:hover:!bg-slate-700"
            data-test="telegram-never-button"
            @click="emit('never')"
          >
            {{ t('telegramModal.neverShow') }}
          </v-btn>

          <v-btn
            variant="outlined"
            class="rounded-lg !normal-case font-semibold !border-sky-500 !text-sky-700 hover:!bg-sky-50 dark:!border-sky-400 dark:!text-sky-300 dark:hover:!bg-slate-800"
            data-test="telegram-later-button"
            @click="emit('later')"
          >
            {{ t('telegramModal.showLater') }}
          </v-btn>
        </div>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { PhTelegramLogo } from '@phosphor-icons/vue'

interface Props {
  modelValue: boolean
  telegramGroupName: string
  invitationLink: string | null
}

const props = defineProps<Props>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  'later': []
  'never': []
}>()

const { t } = useI18n()

const localModel = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value)
})

interface ConfettiPiece {
  id: number
  left: number
  size: number
  delay: number
  duration: number
  drift: number
  rotation: number
  color: string
}

const confettiColors = ['#0ea5e9', '#10b981', '#f59e0b', '#ec4899', '#8b5cf6', '#14b8a6']
const confettiPieces = computed<ConfettiPiece[]>(() =>
  Array.from({ length: 24 }, (_, index) => ({
    id: index,
    left: ((index * 13) % 96) + 2,
    size: 6 + (index % 4),
    delay: (index % 8) * 0.06,
    duration: 1.9 + (index % 5) * 0.18,
    drift: ((index % 2 === 0 ? 1 : -1) * (14 + (index % 6) * 8)),
    rotation: 140 + (index % 7) * 70,
    color: confettiColors[index % confettiColors.length]
  }))
)

const showConfetti = ref(false)
const confettiRunId = ref(0)
let confettiTimer: ReturnType<typeof setTimeout> | null = null

const stopConfettiTimer = () => {
  if (confettiTimer) {
    clearTimeout(confettiTimer)
    confettiTimer = null
  }
}

const triggerConfetti = () => {
  stopConfettiTimer()
  confettiRunId.value += 1
  showConfetti.value = true
  confettiTimer = setTimeout(() => {
    showConfetti.value = false
    confettiTimer = null
  }, 2800)
}

watch(
  () => props.modelValue,
  (isOpen) => {
    if (isOpen) {
      triggerConfetti()
      return
    }

    showConfetti.value = false
    stopConfettiTimer()
  },
  { immediate: true }
)

onBeforeUnmount(() => {
  stopConfettiTimer()
})

const confettiStyle = (piece: ConfettiPiece): Record<string, string> => ({
  left: `${piece.left}%`,
  width: `${piece.size}px`,
  height: `${Math.max(5, Math.floor(piece.size * 0.48))}px`,
  'background-color': piece.color,
  'animation-delay': `${piece.delay}s`,
  'animation-duration': `${piece.duration}s`,
  '--confetti-drift': `${piece.drift}px`,
  '--confetti-rotate': `${piece.rotation}deg`
})

const qrCodeUrl = computed(() => {
  if (!props.invitationLink) {
    return ''
  }
  const encoded = encodeURIComponent(props.invitationLink)
  return `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encoded}`
})
</script>

<style scoped>
.confetti-piece {
  position: absolute;
  top: -20px;
  border-radius: 2px;
  opacity: 0;
  animation-name: confetti-fall;
  animation-timing-function: cubic-bezier(0.2, 0.8, 0.2, 1);
  animation-fill-mode: forwards;
}

@keyframes confetti-fall {
  0% {
    transform: translate3d(0, -8px, 0) rotate(0deg);
    opacity: 0;
  }

  10% {
    opacity: 1;
  }

  100% {
    transform: translate3d(var(--confetti-drift), 180px, 0) rotate(var(--confetti-rotate));
    opacity: 0;
  }
}
</style>
